'use client'

import { useEffect, useState } from 'react'
import { useSearch } from '@/src/contexts'
import { useCurrentLocation } from '@/src/hooks'

import { Button, Card, Checkbox, Typography } from 'antd'
import {
  DistanceFilter,
  GenderFilter,
  GeoLocationButton,
  InsuranceSelect,
  LanguageFilter,
} from '@/src/components'

import { DEFAULT_DISTANCE } from '@/src/data'
import { formatLocationAddress } from '@/src/utils'

import { config } from '@fortawesome/fontawesome-svg-core'
import '@fortawesome/fontawesome-svg-core/styles.css'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import '@/src/lib'
config.autoAddCss = false

const { Text, Title } = Typography

import { QueryStringParams } from '@/src/interfaces'
import type { CheckboxProps } from 'antd'
import type { Query } from '../../../interfaces'

interface FilterCardProps {
  title: string
  children: React.ReactNode
  onClear: () => void
  showClear?: boolean
}

const FilterCard = ({
  title,
  children,
  onClear,
  showClear = true,
}: FilterCardProps) => (
  <Card style={{ borderColor: 'lightGray' }}>
    <div className='flex flex-col gap-2 w-[320px]'>
      <div className='flex justify-between items-center'>
        <Title level={5} style={{ margin: 0, padding: 0 }}>
          {title}
        </Title>
        {showClear && (
          <Button
            type='link'
            size='small'
            onClick={onClear}
            style={{ margin: 0, padding: 0 }}
          >
            Clear
          </Button>
        )}
      </div>
      {children}
    </div>
  </Card>
)

interface SideFilterProps {
  searchParams: QueryStringParams
  setSearchParams: (params: QueryStringParams) => void
}

export const SideFilter = ({
  searchParams,
  setSearchParams,
}: SideFilterProps) => {
  const { query, setQuery, shouldClearSearch, setShouldClearSearch } =
    useSearch()
  const [nonUSMessage, setNonUSMessage] = useState<string>('')

  const {
    geoLocation,
    addressData,
    loadingGeoLocation,
    geoLocationError,
    fetchLocation,
    getCoordinatesFromAddress,
    clearLocation,
    isUS,
  } = useCurrentLocation()

  const updateCheckBoxedParameter = (
    key: keyof Query['checkBoxedParameters']
  ) => {
    return (value: boolean) => {
      setQuery({
        ...query,
        checkBoxedParameters: {
          ...query.checkBoxedParameters,
          [key]: value,
        },
      })
    }
  }

  const fullQueryAddress = formatLocationAddress(query.locationParameters)

  const handleAcceptsNewPatientsChange: CheckboxProps['onChange'] = (e) => {
    updateCheckBoxedParameter('accepts_new_patients')(e.target.checked)
  }

  const handleBookingUrlChange: CheckboxProps['onChange'] = (e) => {
    updateCheckBoxedParameter('booking_url')(e.target.checked)
  }

  const handleTelehealthChange: CheckboxProps['onChange'] = (e) => {
    updateCheckBoxedParameter('telehealth_appointment')(e.target.checked)
  }

  const handleGenderFilterChange = (value: string) => {
    setQuery((prevQuery) => ({
      ...prevQuery,
      genderParameters: value,
    }))
  }

  const handleInsuranceChange = (value: string) => {
    setQuery({
      ...query,
      insuranceParameters: value,
    })
  }

  const handleLanguageFilterChange = (value: string) => {
    setQuery((prevQuery) => ({
      ...prevQuery,
      languageParameters: value,
    }))
  }

  const clearCheckBoxedParameter = (
    key: keyof Query['checkBoxedParameters']
  ) => {
    return () => updateCheckBoxedParameter(key)(false)
  }

  const clearInsurance = () => {
    setQuery({
      ...query,
      insuranceParameters: '',
    })
  }

  const clearDistance = () => {
    const hasAddress = Boolean(addressData?.fullAddress)
    const isDefaultDistance =
      query.geoDistanceParameters.distance === DEFAULT_DISTANCE

    // If there's an address or location parameters, clear location data
    if (
      hasAddress ||
      query.locationParameters?.city ||
      query.locationParameters?.state
    ) {
      clearLocation()
      setQuery((prevQuery) => ({
        ...prevQuery,
        locationParameters: {
          city: '',
          state: '',
          zip: '',
        },
        geoDistanceParameters: {
          distance: DEFAULT_DISTANCE,
          currentLocation: { lat: 0, lon: 0 },
        },
      }))
      setShouldClearSearch(true)
      return
    }

    // If only distance is different from default, just reset the distance
    if (!isDefaultDistance) {
      setQuery((prev) => ({
        ...prev,
        geoDistanceParameters: {
          ...prev.geoDistanceParameters,
          distance: DEFAULT_DISTANCE,
        },
      }))
    }
  }

  useEffect(() => {
    if (shouldClearSearch) {
      clearLocation()
    }
  }, [shouldClearSearch])

  useEffect(() => {
    if (
      !query.geoDistanceParameters?.currentLocation ||
      query.geoDistanceParameters.currentLocation.lat === 0 ||
      query.geoDistanceParameters.currentLocation.lon === 0
    ) {
      setNonUSMessage('')
      return
    }

    if (addressData) {
      if (isUS) {
        setNonUSMessage('')
      } else {
        setNonUSMessage(
          "It seems you're not in the US. We will set a default New York address."
        )
      }
    }
  }, [query.geoDistanceParameters?.currentLocation, addressData])

  const geoLocationAddress =
    formatLocationAddress(addressData) && nonUSMessage === ''
      ? formatLocationAddress(addressData)
      : ''

  const shouldShowAddress =
    fullQueryAddress !== '' && geoLocationAddress === fullQueryAddress

  return (
    <div className='flex flex-col gap-4'>
      <FilterCard
        title='My Insurance Network'
        onClear={clearInsurance}
        showClear={false}
      >
        <InsuranceSelect
          value={query.insuranceParameters}
          onChange={handleInsuranceChange}
        />
      </FilterCard>
      <FilterCard
        title='Location'
        onClear={
          query.geoDistanceParameters.distance !== DEFAULT_DISTANCE ||
          addressData?.fullAddress !== ''
            ? clearDistance
            : () => {}
        }
      >
        {fullQueryAddress && (
          <div className='flex flex-wrap items-center gap-2 mb-4'>
            <Text>
              Within <DistanceFilter /> of{' '}
              {!shouldShowAddress && <Text>{fullQueryAddress}</Text>}
            </Text>
          </div>
        )}
        <GeoLocationButton
          geoLocation={geoLocation}
          addressData={addressData}
          loadingGeoLocation={loadingGeoLocation}
          geoLocationError={geoLocationError}
          fetchLocation={fetchLocation}
          getCoordinatesFromAddress={getCoordinatesFromAddress}
          nonUSMessage={nonUSMessage}
          setNonUSMessage={setNonUSMessage}
          shouldShowAddress={shouldShowAddress}
          geoLocationAddress={geoLocationAddress}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          isUS={isUS}
        />
      </FilterCard>

      <FilterCard
        title='Accepting New Patients'
        onClear={clearCheckBoxedParameter('accepts_new_patients')}
      >
        <Checkbox
          id='accepts-new-patients'
          onChange={handleAcceptsNewPatientsChange}
          checked={query.checkBoxedParameters?.accepts_new_patients ?? false}
        >
          <FontAwesomeIcon icon='calendar-check' className='mr-2' />
          Accepting New Patients
        </Checkbox>
      </FilterCard>

      <FilterCard
        title='Request Appointment Online'
        onClear={clearCheckBoxedParameter('booking_url')}
      >
        <Checkbox
          id='request-appointment-online'
          onChange={handleBookingUrlChange}
          checked={query.checkBoxedParameters?.booking_url ?? false}
        >
          <FontAwesomeIcon icon='calendar-days' className='mr-2' />
          Request Appointment Online
        </Checkbox>
      </FilterCard>

      <FilterCard
        title='Offers Telehealth'
        onClear={clearCheckBoxedParameter('telehealth_appointment')}
      >
        <Checkbox
          id='offers-telehealth'
          onChange={handleTelehealthChange}
          checked={query.checkBoxedParameters?.telehealth_appointment ?? false}
        >
          <FontAwesomeIcon icon='desktop' className='mr-2' />
          Offers Telehealth
        </Checkbox>
      </FilterCard>

      <FilterCard
        title='Physician Gender'
        onClear={() => handleGenderFilterChange('')}
      >
        <GenderFilter
          selectedGender={query.genderParameters || ''}
          onChange={handleGenderFilterChange}
        />
      </FilterCard>
      <FilterCard
        title='Language'
        onClear={() => handleLanguageFilterChange('')}
      >
        <LanguageFilter
          selectedLanguage={query.languageParameters || ''}
          onChange={handleLanguageFilterChange}
        />
      </FilterCard>
    </div>
  )
}
